import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import type { DefaultError } from '@tanstack/react-query'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { GhostButton } from '../../components/buttons/ghost-button'
import { RadioGroup, RadioItem } from '../../components/input/radio.tsx'
import { SearchableCombobox } from '../../components/input/searchable-combobox.tsx'
import { SelectBox } from '../../components/input/select-box.tsx'
import { TextInput } from '../../components/input/text-input.tsx'
import { SCMIcon } from '../../components/third-party-branding/scm-icon.tsx'
import {
  useAllIntegrations,
  useGetAllAzureOrganizationPages,
  useGetAllAzureProjectPages,
  useGetAllAzureRepositoryPages,
  useGetBitbucketRepositories,
  useGetBitbucketWorkspaces,
  useGetGitLabProjects,
} from '../../utils/api-client/api-hooks.ts'
import type { AddRepositoryRequest, Integration } from '../../utils/api-client/api-schemas'
import { getErrorMessage } from '../../utils/error-handling/utils.ts'
import type { EnvironmentName } from '../../utils/hooks/get-environment-data.ts'
import * as styles from './add-repository-form.css'

export type ConnectionType = 'GitHubApp' | 'GenericGit' | 'Integration'
export type IntegrationType = 'azureDevOps' | 'gitlab' | 'bitbucket'

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

type AddRepositoryFormValues = {
  selectedConnectionType: ConnectionType
  selectedIntegration: IntegrationType
  url: string
  username: string
  password: string
  selectedGitHubIntegrationId: string | undefined
  selectedAzureIntegrationId: string | undefined
  selectedAzureOrganizationId: string | undefined
  selectedAzureProjectId: string | undefined
  selectedAzureRepositoryId: string | undefined
  gitLabProjectSearchQuery: string
  selectedGitLabIntegrationId: string | undefined
  selectedBitbucketIntegrationId: string | undefined
  selectedBitbucketWorkspaceSlug: string | undefined
  selectedBitbucketRepositorySlug: string | undefined
}

const FORM_DEFAULT_VALUES: AddRepositoryFormValues = {
  selectedConnectionType: 'GitHubApp',
  selectedIntegration: 'azureDevOps',
  url: '',
  username: '',
  password: '',
  selectedGitHubIntegrationId: undefined,
  selectedAzureIntegrationId: undefined,
  selectedAzureOrganizationId: undefined,
  selectedAzureProjectId: undefined,
  selectedAzureRepositoryId: undefined,
  gitLabProjectSearchQuery: '',
  selectedGitLabIntegrationId: undefined,
  selectedBitbucketIntegrationId: undefined,
  selectedBitbucketWorkspaceSlug: undefined,
  selectedBitbucketRepositorySlug: undefined,
}

export const useAddRepositoryForm = (defaultValueOverrides: Partial<AddRepositoryFormValues>) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

export type AddRepositoryFormProps = {
  environmentName: EnvironmentName
  onClose: () => unknown
  githubAppName: string | undefined
  isAzureIntegrationEnabled: boolean
  isGitlabIntegrationEnabled: boolean
  isBitbucketIntegrationEnabled: boolean
  addRepository: (params: AddRepositoryRequest, options?: { onSuccess?: () => unknown }) => unknown
}

export const AddRepositoryForm: React.FC<AddRepositoryFormProps> = ({
  environmentName,
  onClose,
  githubAppName,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
  addRepository,
}) => {
  const form = useFormContext()
  const selectedConnectionType = useSelector(form.store, s => s.values.selectedConnectionType)

  return (
    <form className={styles.form}>
      <ConnectionTypeRadioInput
        environmentName={environmentName}
        isGitHubIntegrationEnabled={!!githubAppName}
        isAzureIntegrationEnabled={isAzureIntegrationEnabled}
        isGitlabIntegrationEnabled={isGitlabIntegrationEnabled}
        isBitbucketIntegrationEnabled={isBitbucketIntegrationEnabled}
      />
      {match(selectedConnectionType)
        .with('GitHubApp', () =>
          githubAppName ? <PixeebotAppConnector onClose={onClose} githubAppName={githubAppName} /> : null
        )
        .with('GenericGit', () => (
          <>
            <div className={styles.inputGroup}>
              <GenericGitFields />
            </div>
            <div className={styles.buttonGroup}>
              <GhostButton onClick={onClose}>Cancel</GhostButton>
              <GenericGitConnectButton addRepository={addRepository} />
            </div>
          </>
        ))
        .with('Integration', () =>
          isAzureIntegrationEnabled || isGitlabIntegrationEnabled || isBitbucketIntegrationEnabled ? (
            <IntegrationConnector
              onClose={onClose}
              isAzureIntegrationEnabled={isAzureIntegrationEnabled}
              isGitlabIntegrationEnabled={isGitlabIntegrationEnabled}
              isBitbucketIntegrationEnabled={isBitbucketIntegrationEnabled}
              addRepository={addRepository}
            />
          ) : null
        )
        .exhaustive()}
    </form>
  )
}

type ConnectionTypeRadioInputProps = Pick<
  AddRepositoryFormProps,
  'environmentName' | 'isAzureIntegrationEnabled' | 'isGitlabIntegrationEnabled' | 'isBitbucketIntegrationEnabled'
> & {
  isGitHubIntegrationEnabled: boolean
}

const ConnectionTypeRadioInput: React.FC<ConnectionTypeRadioInputProps> = ({
  environmentName,
  isGitHubIntegrationEnabled,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
}) => {
  const form = useFormContext()
  const selectedConnectionType = useSelector(form.store, s => s.values.selectedConnectionType)

  return (
    <RadioGroup
      value={selectedConnectionType}
      onValueChange={type => form.setFieldValue('selectedConnectionType', type as ConnectionType)}
      className={styles.radioGroup}
    >
      {isGitHubIntegrationEnabled && (
        <RadioItem
          value="GitHubApp"
          id="github-app"
          label={`Install the ${environmentName === 'ntt' ? 'Github' : 'Pixeebot GitHub'} application (GitHub repositories only)`}
        />
      )}
      <RadioItem value="GenericGit" id="generic-git" label="Connect via Git client (clone a repository from any SCM)" />
      {(isAzureIntegrationEnabled || isGitlabIntegrationEnabled || isBitbucketIntegrationEnabled) && (
        <RadioItem value="Integration" id="integration" label="Connect to repositories from an existing integration" />
      )}
    </RadioGroup>
  )
}

type PixeebotAppConnectorProps = Pick<AddRepositoryFormProps, 'onClose' | 'githubAppName'>

const PixeebotAppConnector: React.FC<PixeebotAppConnectorProps> = ({ onClose, githubAppName }) => {
  const form = useFormContext()
  const selectedGitHubIntegrationId = useSelector(form.store, s => s.values.selectedGitHubIntegrationId)
  const { connections, selectedId } = useSelectedConnection('github', selectedGitHubIntegrationId)

  // Which App the installation link points at. A GitHub repository is not picked from a list the
  // way the other three providers' are: it arrives when someone installs the App, so the choice a
  // user makes here is which App to install, and each connection is a different App.
  const selected = connections.find(connection => connection.id === selectedId)
  const appName = (selected?.type === 'github' ? selected.app_name : undefined) ?? githubAppName

  return (
    <>
      <div className={styles.inputGroup}>
        <ConnectionSelect
          label="GitHub connection"
          connections={connections}
          selectedId={selectedId}
          onSelect={value => form.setFieldValue('selectedGitHubIntegrationId', value)}
        />
      </div>
      <div className={styles.buttonGroup}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <DefaultButton
          href={`https://github.com/apps/${appName}/installations/new`}
          buttonType="primary"
          onClick={onClose}
        >
          Go to GitHub
        </DefaultButton>
      </div>
    </>
  )
}

type IntegrationConnectorProps = Pick<
  AddRepositoryFormProps,
  | 'onClose'
  | 'isAzureIntegrationEnabled'
  | 'isGitlabIntegrationEnabled'
  | 'isBitbucketIntegrationEnabled'
  | 'addRepository'
>

const IntegrationConnector: React.FC<IntegrationConnectorProps> = ({
  onClose,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
  addRepository,
}) => {
  const form = useFormContext()
  const selectedIntegration = useSelector(form.store, s => s.values.selectedIntegration)

  const integrationOptions = useMemo(() => {
    function* generateOptions() {
      if (isAzureIntegrationEnabled) {
        yield {
          value: 'azureDevOps' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="azure" size="medium" />
              Azure DevOps
            </span>
          ),
        }
      }
      if (isGitlabIntegrationEnabled) {
        yield {
          value: 'gitlab' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="gitlab" size="medium" />
              GitLab
            </span>
          ),
        }
      }
      if (isBitbucketIntegrationEnabled) {
        yield {
          value: 'bitbucket' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="bitbucket" size="medium" />
              Bitbucket
            </span>
          ),
        }
      }
    }
    return [...generateOptions()]
  }, [isAzureIntegrationEnabled, isGitlabIntegrationEnabled, isBitbucketIntegrationEnabled])

  return (
    <>
      <div className={styles.inputGroup} aria-label="Integration selections">
        <SelectBox
          label="Integration"
          value={selectedIntegration}
          onValueChange={value => {
            form.setFieldValue('selectedIntegration', value as IntegrationType)
          }}
          options={integrationOptions}
        />
        {match(selectedIntegration)
          .with('azureDevOps', () => (isAzureIntegrationEnabled ? <AzureDevOpsFields /> : null))
          .with('gitlab', () => (isGitlabIntegrationEnabled ? <GitLabFields /> : null))
          .with('bitbucket', () => (isBitbucketIntegrationEnabled ? <BitbucketFields /> : null))
          .exhaustive()}
      </div>
      <div className={styles.buttonGroup}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        {match(selectedIntegration)
          .with('azureDevOps', () =>
            isAzureIntegrationEnabled ? <AzureDevOpsConnectButton addRepository={addRepository} /> : null
          )
          .with('gitlab', () =>
            isGitlabIntegrationEnabled ? <GitLabConnectButton addRepository={addRepository} /> : null
          )
          .with('bitbucket', () =>
            isBitbucketIntegrationEnabled ? <BitbucketConnectButton addRepository={addRepository} /> : null
          )
          .exhaustive()}
      </div>
    </>
  )
}

const useSelectedAzureConnection = () => {
  const form = useFormContext()
  const selected = useSelector(form.store, s => s.values.selectedAzureIntegrationId)
  return useSelectedConnection('azure', selected)
}

/**
 * The message to show under a picker whose list could not be fetched.
 *
 * <p>A failed lookup leaves the list empty, which the user reads as "this connection can see
 * nothing" — the same thing an empty account looks like. `getErrorMessage` is the text the mutation
 * toasts already use, so a problem detail from the API is shown verbatim and anything else falls
 * back to the standard sentence.
 *
 * @param errors the `error` of each query behind the picker, in the order they were issued
 * @returns the first failure's message, or undefined when none failed
 */
const pickerError = (...errors: (DefaultError | null)[]) => {
  const failure = errors.find(error => error !== null && error !== undefined)
  return failure ? getErrorMessage(failure) : undefined
}

const AzureDevOpsFields: React.FC = () => {
  const form = useFormContext()
  const selectedAzureOrganizationId = useSelector(form.store, s => s.values.selectedAzureOrganizationId)
  const selectedAzureProjectId = useSelector(form.store, s => s.values.selectedAzureProjectId)
  const selectedAzureRepositoryId = useSelector(form.store, s => s.values.selectedAzureRepositoryId)
  const { connections, selectedId } = useSelectedAzureConnection()

  const azureOrganizationResults = useGetAllAzureOrganizationPages({ integrationId: selectedId })
  const azureProjectResults = useGetAllAzureProjectPages({
    integrationId: selectedId,
    organizationId: selectedAzureOrganizationId,
  })
  const azureRepositoryResults = useGetAllAzureRepositoryPages({
    integrationId: selectedId,
    organizationName: selectedAzureOrganizationId ?? '',
    projectId: selectedAzureProjectId ?? '',
    enabled: !!selectedAzureOrganizationId && !!selectedAzureProjectId,
  })

  const azureOrganizations = azureOrganizationResults.flatMap(r => r.data?._embedded?.items ?? [])
  const azureProjects = azureProjectResults.flatMap(r => r.data?._embedded?.items ?? [])
  const azureRepositories = azureRepositoryResults.flatMap(r => r.data?._embedded?.items ?? [])

  const azureOrganizationOptions = azureOrganizations.map(org => ({
    value: org.name,
    label: org.name,
  }))
  const azureProjectOptions = azureProjects.map(project => ({
    value: project.id,
    label: project.name,
  }))
  const azureRepositoryOptions = azureRepositories.map(repo => ({
    value: repo.id,
    label: repo.name,
    disabled: repo.pixee_id !== undefined,
  }))

  return (
    <>
      <ConnectionSelect
        label="Azure DevOps connection"
        connections={connections}
        selectedId={selectedId}
        onSelect={value => {
          // The organizations, projects and repositories below all belong to the previous
          // connection, so the whole chain is cleared along with the choice.
          form.setFieldValue('selectedAzureIntegrationId', value)
          form.setFieldValue('selectedAzureOrganizationId', '')
          form.setFieldValue('selectedAzureProjectId', '')
          form.setFieldValue('selectedAzureRepositoryId', '')
        }}
      />
      <SelectBox
        label="Organization"
        value={selectedAzureOrganizationId}
        onValueChange={value => {
          form.setFieldValue('selectedAzureOrganizationId', value)
          form.setFieldValue('selectedAzureProjectId', '')
          form.setFieldValue('selectedAzureRepositoryId', '')
        }}
        placeholder="Select organization"
        options={azureOrganizationOptions}
        errorText={pickerError(...azureOrganizationResults.map(result => result.error))}
      />
      <SelectBox
        label="Project"
        value={selectedAzureProjectId}
        onValueChange={value => {
          form.setFieldValue('selectedAzureProjectId', value)
          form.setFieldValue('selectedAzureRepositoryId', '')
        }}
        disabled={!selectedAzureOrganizationId}
        placeholder="Select project"
        options={azureProjectOptions}
        errorText={pickerError(...azureProjectResults.map(result => result.error))}
      />
      <SelectBox
        label="Available repositories"
        value={selectedAzureRepositoryId}
        onValueChange={value => form.setFieldValue('selectedAzureRepositoryId', value)}
        disabled={!selectedAzureProjectId}
        placeholder="Select repository"
        options={azureRepositoryOptions}
        errorText={pickerError(...azureRepositoryResults.map(result => result.error))}
      />
    </>
  )
}

type ConnectButtonProps = Pick<AddRepositoryFormProps, 'addRepository'>

const AzureDevOpsConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const selectedAzureOrganizationId = useSelector(form.store, s => s.values.selectedAzureOrganizationId)
  const selectedAzureProjectId = useSelector(form.store, s => s.values.selectedAzureProjectId)
  const selectedAzureRepositoryId = useSelector(form.store, s => s.values.selectedAzureRepositoryId)
  const { selectedId } = useSelectedAzureConnection()

  const azureProjectResults = useGetAllAzureProjectPages({
    integrationId: selectedId,
    organizationId: selectedAzureOrganizationId,
  })
  const azureRepositoryResults = useGetAllAzureRepositoryPages({
    integrationId: selectedId,
    organizationName: selectedAzureOrganizationId ?? '',
    projectId: selectedAzureProjectId ?? '',
    enabled: !!selectedAzureOrganizationId && !!selectedAzureProjectId,
  })

  const azureProjects = azureProjectResults.flatMap(r => r.data?._embedded?.items ?? [])
  const azureRepositories = azureRepositoryResults.flatMap(r => r.data?._embedded?.items ?? [])

  const selectedAzureRepository = azureRepositories.find(repo => repo.id === selectedAzureRepositoryId)
  const selectedAzureProject = azureProjects.find(proj => proj.id === selectedAzureProjectId)

  return (
    <DefaultButton
      buttonType="primary"
      state={selectedAzureRepositoryId ? 'default' : 'disabled'}
      onClick={() => {
        if (selectedAzureOrganizationId && selectedAzureProject && selectedAzureRepository) {
          addRepository({
            type: 'azure',
            organization: selectedAzureOrganizationId,
            project: selectedAzureProject.name,
            name: selectedAzureRepository.name,
            // Which connection the repository was listed through. The backend clones from that
            // connection's server and records it, so every later call about this repository
            // reaches the same Azure DevOps.
            integration_id: selectedId,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const useSelectedBitbucketConnection = () => {
  const form = useFormContext()
  const selected = useSelector(form.store, s => s.values.selectedBitbucketIntegrationId)
  return useSelectedConnection('bitbucket', selected)
}

const BitbucketFields: React.FC = () => {
  const form = useFormContext()
  const selectedBitbucketWorkspaceSlug = useSelector(form.store, s => s.values.selectedBitbucketWorkspaceSlug)
  const selectedBitbucketRepositorySlug = useSelector(form.store, s => s.values.selectedBitbucketRepositorySlug)
  const { connections, selectedId } = useSelectedBitbucketConnection()

  const { data: bitbucketWorkspaces, error: bitbucketWorkspacesError } = useGetBitbucketWorkspaces({
    integrationId: selectedId,
  })
  const { data: bitbucketRepositories, error: bitbucketRepositoriesError } = useGetBitbucketRepositories({
    integrationId: selectedId,
    workspaceSlug: selectedBitbucketWorkspaceSlug!,
    enabled: !!selectedBitbucketWorkspaceSlug,
  })

  const bitbucketWorkspaceOptions = (bitbucketWorkspaces?._embedded?.items ?? []).map(workspace => ({
    value: workspace.slug,
    label: workspace.name,
  }))

  const bitbucketRepositoryOptions = (bitbucketRepositories?._embedded?.items ?? []).map(repo => ({
    value: repo.slug,
    label: repo.name,
    disabled: repo.pixee_id !== undefined,
  }))

  return (
    <>
      <ConnectionSelect
        label="Bitbucket connection"
        connections={connections}
        selectedId={selectedId}
        onSelect={value => {
          // The workspaces and repositories below belong to the previous connection's Atlassian
          // account, so both are cleared along with the choice.
          form.setFieldValue('selectedBitbucketIntegrationId', value)
          form.setFieldValue('selectedBitbucketWorkspaceSlug', undefined)
          form.setFieldValue('selectedBitbucketRepositorySlug', undefined)
        }}
      />
      <SelectBox
        label="Workspace"
        value={selectedBitbucketWorkspaceSlug}
        onValueChange={value => {
          form.setFieldValue('selectedBitbucketWorkspaceSlug', value)
          form.setFieldValue('selectedBitbucketRepositorySlug', undefined)
        }}
        placeholder="Select workspace"
        options={bitbucketWorkspaceOptions}
        errorText={pickerError(bitbucketWorkspacesError)}
      />
      <SelectBox
        label="Repository"
        value={selectedBitbucketRepositorySlug}
        onValueChange={value => form.setFieldValue('selectedBitbucketRepositorySlug', value)}
        disabled={!selectedBitbucketWorkspaceSlug}
        placeholder="Select repository"
        options={bitbucketRepositoryOptions}
        errorText={pickerError(bitbucketRepositoriesError)}
      />
    </>
  )
}

const BitbucketConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const selectedBitbucketWorkspaceSlug = useSelector(form.store, s => s.values.selectedBitbucketWorkspaceSlug)
  const selectedBitbucketRepositorySlug = useSelector(form.store, s => s.values.selectedBitbucketRepositorySlug)
  const { selectedId } = useSelectedBitbucketConnection()

  return (
    <DefaultButton
      buttonType="primary"
      state={selectedBitbucketRepositorySlug ? 'default' : 'disabled'}
      onClick={() => {
        if (selectedBitbucketWorkspaceSlug && selectedBitbucketRepositorySlug) {
          addRepository({
            type: 'bitbucket',
            workspace: selectedBitbucketWorkspaceSlug,
            slug: selectedBitbucketRepositorySlug,
            // Which connection the repository was listed through, so every later call about it
            // authenticates as the same Bitbucket account.
            integration_id: selectedId,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

/**
 * The connections of one type the installation has, and which one the form is acting on.
 *
 * Falls back to the first when the user has not chosen, so an installation with exactly one
 * behaves as it did when one was all there could be: no choice to make, nothing to pick.
 */
const useSelectedConnection = (type: Integration['type'], selectedId: string | undefined) => {
  const { integrations } = useAllIntegrations()
  const connections = useMemo(() => integrations.filter(integration => integration.type === type), [integrations, type])
  return { connections, selectedId: selectedId ?? connections[0]?.id }
}

/**
 * A picker over the connections of one type, rendered only when there is a choice to make.
 */
const ConnectionSelect: React.FC<{
  label: string
  connections: Integration[]
  selectedId: string | undefined
  onSelect: (id: string) => void
}> = ({ label, connections, selectedId, onSelect }) =>
  connections.length > 1 ? (
    <SelectBox
      label={label}
      value={selectedId}
      onValueChange={onSelect}
      options={connections.map(connection => ({ value: connection.id, label: connection.name }))}
    />
  ) : null

const useSelectedGitLabConnection = () => {
  const form = useFormContext()
  const selected = useSelector(form.store, s => s.values.selectedGitLabIntegrationId)
  return useSelectedConnection('gitlab', selected)
}

const GitLabConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const gitLabProjectSearchQuery = useSelector(form.store, s => s.values.gitLabProjectSearchQuery)
  const { selectedId } = useSelectedGitLabConnection()

  const { data: gitlabProjectsData } = useGetGitLabProjects({
    integrationId: selectedId,
    search: gitLabProjectSearchQuery || undefined,
    enabled: true,
  })
  const gitlabProjects = gitlabProjectsData?._embedded?.items ?? []
  const selectedGitLabProject = gitlabProjects.find(project => project.name_with_namespace === gitLabProjectSearchQuery)

  return (
    <DefaultButton
      buttonType="primary"
      state={selectedGitLabProject ? 'default' : 'disabled'}
      onClick={() => {
        if (selectedGitLabProject) {
          addRepository({
            type: 'gitlab',
            name: selectedGitLabProject.path,
            namespace: selectedGitLabProject.namespace_full_path,
            // Which connection the repository was listed through. The backend clones from that
            // connection's server and records it, so every later call about this repository
            // reaches the same GitLab.
            integration_id: selectedId,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const GitLabFields: React.FC = () => {
  const form = useFormContext()
  const { connections, selectedId } = useSelectedGitLabConnection()

  return (
    <>
      <ConnectionSelect
        label="GitLab connection"
        connections={connections}
        selectedId={selectedId}
        onSelect={value => {
          // The projects listed belong to the previous connection, so the search term is cleared
          // along with the choice rather than left pointing at a project on another server.
          form.setFieldValue('selectedGitLabIntegrationId', value)
          form.setFieldValue('gitLabProjectSearchQuery', '')
        }}
      />
      <GitLabSearchableCombobox />
    </>
  )
}

const GitLabSearchableCombobox: React.FC = () => {
  const form = useFormContext()
  const gitLabProjectSearchQuery = useSelector(form.store, s => s.values.gitLabProjectSearchQuery)
  const { selectedId } = useSelectedGitLabConnection()

  const {
    data: gitlabProjectsData,
    isFetching: isGitlabProjectsFetching,
    error: gitlabProjectsError,
  } = useGetGitLabProjects({
    integrationId: selectedId,
    search: gitLabProjectSearchQuery || undefined,
    enabled: true,
  })
  const gitlabProjects = gitlabProjectsData?._embedded?.items ?? []

  const gitlabProjectOptions = useMemo(
    () =>
      gitlabProjects.map(project => ({
        path: project.path,
        value: project.name_with_namespace,
        label: project.name_with_namespace,
        enabled: project.pixee_id === undefined,
        checked: project.pixee_id !== undefined,
      })),
    [gitlabProjects]
  )

  return (
    <SearchableCombobox
      label="Available projects"
      placeholder="Search GitLab projects..."
      searchValue={gitLabProjectSearchQuery}
      setSearchValue={value => form.setFieldValue('gitLabProjectSearchQuery', value)}
      options={gitlabProjectOptions}
      isFetching={isGitlabProjectsFetching}
      errorText={pickerError(gitlabProjectsError)}
      side="bottom"
      avoidCollisions={false}
    />
  )
}

const GenericGitFields: React.FC = () => {
  const form = useFormContext()
  const url = useSelector(form.store, s => s.values.url)
  const username = useSelector(form.store, s => s.values.username)
  const password = useSelector(form.store, s => s.values.password)

  return (
    <>
      <TextInput
        label="URL"
        id="repository-url"
        name="url"
        placeholder="ex: https://scm.com/org/repo-lang.git"
        value={url}
        onChange={value => form.setFieldValue('url', value)}
        isRequired
      />
      <TextInput
        label="Username"
        id="repository-username"
        name="username"
        placeholder="Enter username"
        value={username}
        onChange={value => form.setFieldValue('username', value)}
        explicitLabelCondition="optional"
      />
      <TextInput
        type="password"
        label="Password"
        id="repository-password"
        name="password"
        placeholder="Enter password"
        value={password}
        onChange={value => form.setFieldValue('password', value)}
        explicitLabelCondition="optional"
      />
    </>
  )
}

const GenericGitConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const url = useSelector(form.store, s => s.values.url)
  const username = useSelector(form.store, s => s.values.username)
  const password = useSelector(form.store, s => s.values.password)

  const extractRepoName = (repoUrl: string): string => {
    const match = repoUrl.match(/\/([^/]+)\.git$/)
    return match ? (match[1] ?? repoUrl) : repoUrl
  }

  return (
    <DefaultButton
      buttonType="primary"
      state={url ? 'default' : 'disabled'}
      onClick={() =>
        addRepository(
          {
            type: 'git',
            name: extractRepoName(url),
            credentials: { username, password },
            url,
          },
          {
            onSuccess: () => {
              form.setFieldValue('url', '')
              form.setFieldValue('username', '')
              form.setFieldValue('password', '')
            },
          }
        )
      }
    >
      Connect
    </DefaultButton>
  )
}
